import {
  ONBOARDING_STEP_COPY,
  type FlagKey,
  type MeResponse,
  type OnboardingStepCopy,
  type OnboardingStepNumber,
  type OnboardingStepState,
} from '@doneverse/shared'
import { getRequiredFlag } from '@/hooks/useFeatureFlagRouteGuard'
import { getHomeRoute, matchesPathPrefix } from '@/lib/routes'

/**
 * Access policy for a founder who has not finished onboarding steps 1–4
 * (ClickUp 86d3uknt8).
 *
 * Modelled on `lib/doerAccess`: one module owns the rule, the router enforces
 * it, and the menu reflects it, so the guard and the chrome can never drift.
 * The difference is that a locked founder is mid-journey rather than
 * mis-provisioned — the allowlist is therefore derived from where they are in
 * the journey, not a fixed list of paths.
 *
 * The lock is derived from the profile rather than a hook so the router's
 * `beforeLoad` can enforce it before a locked page ever paints, and the rollout
 * flag is folded in HERE for the same reason `useOnboarding` folds it in there:
 * a lock that fires for someone who cannot see the unlock UI is a trap. The API
 * already omits `onboarding` for non-founders and the grandfathered cohort, so
 * those users land on the unlocked branch without a role check.
 */

/** Shown on every nav item the lock disables. */
export const ONBOARDING_LOCK_MESSAGE = 'Complete your onboarding to unlock this feature.'

/** The rollout flag for the whole founder-onboarding feature. */
export const ONBOARDING_FLAG: FlagKey = 'founder-onboarding'

/**
 * Where a locked founder lands when their active step has no surface of its own
 * (steps 1 and 4) — the founder home, which like every page leads with the
 * onboarding panel that explains the step.
 *
 * Derived rather than re-typed: the lock only ever applies to founders, so this
 * IS the founder home, and `lib/routes` is the one place that decides where a
 * role lands. Moving the founder home has to move the lock's allowlist with it.
 */
export const ONBOARDING_LOCKED_HOME = getHomeRoute('founder')

export interface OnboardingLock {
  /** Steps 1–4 are still open: the dashboard is not the founder's to use yet. */
  locked: boolean
  /** The active step's own surface, when it has a reachable one. */
  stepRoute: string | null
  /** Where a blocked navigation is sent. Always itself allowed. */
  redirectTo: string
}

const UNLOCKED: OnboardingLock = {
  locked: false,
  stepRoute: null,
  redirectTo: ONBOARDING_LOCKED_HOME,
}

/**
 * Whether a step's destination is worth sending a founder to.
 *
 * One question only: would the feature-flag route guard bounce them the instant
 * they arrived? So we consult `getRequiredFlag` — the guard's OWN map — rather
 * than a second copy that could drift from what the router actually enforces.
 *
 * Flags the guard does NOT carry are deliberately ignored, even when the flag
 * registry says they gate the same page. `kick-off-call` and
 * `coaching-call-recordings` hide sidebar entries from founders who are
 * browsing; steps 3 and 8 are mandatory and have no founder-side alternative, so
 * treating a nav flag as a barrier would strand a founder on a step only they
 * can complete. Onboarding owns a step's surface while that step is open — see
 * the `route` note on `OnboardingStepCopy`.
 */
export function isStepRouteReachable(
  // Narrowed to the one field the answer depends on so the product tour's stops
  // (lib/productTour) can be judged by this exact predicate rather than a second
  // copy of the rule that drifts from what the router enforces.
  copy: Pick<OnboardingStepCopy, 'route'>,
  hasFlag: (flag: FlagKey) => boolean,
): boolean {
  if (!copy.route) return false
  const guardFlag = getRequiredFlag(copy.route)
  if (guardFlag && !hasFlag(guardFlag)) return false
  return true
}

/**
 * The lock, derived from the profile's server-owned onboarding state plus the
 * flags that decide whether the active step's surface is worth routing to.
 */
export function selectOnboardingLock(
  profile: Pick<MeResponse, 'onboarding'> | null | undefined,
  hasFlag: (flag: FlagKey) => boolean,
): OnboardingLock {
  if (!hasFlag(ONBOARDING_FLAG)) return UNLOCKED
  const state = profile?.onboarding
  if (!state || state.unlocked) return UNLOCKED

  const step = state.nextUp
  const copy = step === null ? null : ONBOARDING_STEP_COPY[step]
  // `route` is non-null once reachability passes; the cast-free read below keeps
  // that obvious.
  const stepRoute = copy && isStepRouteReachable(copy, hasFlag) ? copy.route : null

  return {
    locked: true,
    stepRoute,
    // Steps 1 and 4 have no surface (the overlay IS the step), and a flagged-off
    // surface is no destination either — both fall back to the founder home,
    // where the overlay renders.
    redirectTo: stepRoute ?? ONBOARDING_LOCKED_HOME,
  }
}

/**
 * Whether the founder is standing on the active step's own surface.
 *
 * The one question both halves of the lock turn on, asked here once rather than
 * at each of them: the allowlist lets this page through, and it is the one page
 * whose content is not dimmed. Two copies of the rule could drift apart into a
 * page a founder may reach but not touch.
 *
 * Prefix-matched, so a step's sub-pages count as still on the step.
 */
function isOnStepRoute(pathname: string, lock: OnboardingLock): boolean {
  return lock.stepRoute !== null && matchesPathPrefix(pathname, lock.stepRoute)
}

/**
 * The only surfaces a locked founder may reach: the founder home, where the
 * panel explains the step, and the active step's own surface, where they
 * actually do it. Everything else in the authenticated app is redirected.
 *
 * Deliberately narrow. The panel leads both, but on the step's own surface the
 * page beneath it stays live so the founder can fill in the brief — which is
 * exactly when the rest of the app becomes reachable, and exactly what this
 * closes.
 */
export function isAllowedWhileOnboardingLocked(pathname: string, lock: OnboardingLock): boolean {
  if (!lock.locked) return true
  // Prefix-matched, so the home's sub-pages count as still at home.
  if (matchesPathPrefix(pathname, ONBOARDING_LOCKED_HOME)) return true
  return isOnStepRoute(pathname, lock)
}

/**
 * Whether the page's own content should be dimmed and inert right now.
 *
 * The lock's visible half. `isAllowedWhileOnboardingLocked` decides whether a
 * founder may be on a page at all; this decides what the page looks like once
 * they are on one of the two they are allowed:
 *
 *   the founder home  — the onboarding panel leads it and everything below is
 *                       out of reach, which is the whole "dashboard is locked"
 *                       state (Developer Brief: nav disabled, background dimmed,
 *                       only the active onboarding CTA clickable);
 *   the step's surface — NOT dimmed. It is the page the panel just sent them to
 *                       so they could do the thing it asked for, and a brief
 *                       form under a sheet of glass is the bug this rule exists
 *                       to prevent.
 */
export function isPageContentLocked(pathname: string, lock: OnboardingLock): boolean {
  if (!lock.locked) return false
  return !isOnStepRoute(pathname, lock)
}

/**
 * The step whose page the founder is standing on, if any.
 *
 * What the panel describes when the founder has not asked for something else,
 * and the reason a rail node works on the FIRST press. The panel's own
 * selection is `useState` in a component `PageLayout` remounts on every
 * navigation, so the pick a node makes does not survive the navigation that
 * same node triggers: a panel falling back to `nextUp` would land the founder
 * on the page they asked for with the card still describing the step they left,
 * and only catch up on a second press (which does not navigate, so nothing
 * remounts). Reading the route instead means there is nothing to survive.
 *
 * It also puts the right card up for a founder who arrived some other way — the
 * sidebar, a bookmark, a link from their doer.
 *
 * Prefix-matched, so a playbook's own page still counts as step 7's. Longest
 * route wins, which is what stops step 5's `/` claiming every page in the app
 * if it is ever drawn (the rail excludes it today, but the copy record is
 * total).
 */
export function onboardingStepForPath(
  steps: OnboardingStepState[],
  pathname: string,
): OnboardingStepNumber | null {
  let best: { step: OnboardingStepNumber; length: number } | null = null

  for (const step of steps) {
    const { route } = ONBOARDING_STEP_COPY[step.stepNumber]
    if (!route || !matchesPathPrefix(pathname, route)) continue
    if (best !== null && route.length <= best.length) continue
    best = { step: step.stepNumber, length: route.length }
  }

  return best?.step ?? null
}

/**
 * Where a rail node takes the founder, or null when pressing it may do no more
 * than change which step the panel below describes.
 *
 * The rail is the journey AND a way into it: a founder who presses "Foundations"
 * means "take me back to those videos", not "describe them to me in one line".
 * But only where that is a real place to be, which is three questions rather
 * than one:
 *
 *   is there a destination at all — steps 1 and 4 are in-shell surfaces
 *     (`route: null`), and a rail click must not drop a founder into an
 *     agreement they cannot dismiss. Their CTA opens the surface; the node
 *     selects;
 *   is it reachable — {@link isStepRouteReachable}, which reads the flag
 *     guard's own map;
 *   are they allowed there NOW — {@link isAllowedWhileOnboardingLocked}, the
 *     predicate `beforeLoad` enforces, so the rail can never offer a click the
 *     router would bounce. Before the unlock that is the active step alone;
 *     after it, everything.
 *
 * And a fourth those cannot answer: a step that has not OPENED yet is nowhere
 * to send anyone. The route lock stops caring the moment the dashboard unlocks
 * — `selectOnboardingLock` returns UNLOCKED — so past that point the step's own
 * status is the only thing that still knows step 11 is shut.
 */
export function stepRailDestination(
  step: OnboardingStepState,
  lock: OnboardingLock,
  hasFlag: (flag: FlagKey) => boolean,
): { to: string; hash?: string } | null {
  if (step.status === 'locked') return null
  const copy = ONBOARDING_STEP_COPY[step.stepNumber]
  if (!copy.route || !isStepRouteReachable(copy, hasFlag)) return null
  if (!isAllowedWhileOnboardingLocked(copy.route, lock)) return null
  return { to: copy.route, hash: copy.hash }
}

/** Convenience for menu chrome: is this destination disabled right now? */
export function isNavTargetLocked(to: string | undefined, lock: OnboardingLock): boolean {
  if (!lock.locked) return false
  if (!to) return true
  return !isAllowedWhileOnboardingLocked(to, lock)
}
